import * as React from 'react';
import { cn } from '@/lib/utils';
import { Slot } from '@radix-ui/react-slot';

const ClassroomModuleList = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-classroom-module-list', 'h-[300px] flex-grow space-y-4 overflow-y-auto overflow-x-hidden', className)}
    {...props}
  />
));
ClassroomModuleList.displayName = 'ClassroomModuleList';

const ClassroomModule = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-classroom-module', 'rounded-xl border border-solid border-neutral-lightest shadow-dropshadow-sm', 'relative', className)}
    {...props}
  />
));
ClassroomModule.displayName = 'ClassroomModule';

const ClassroomModuleHeader = React.forwardRef(({ className, ...props }, ref) => (
  <header
    ref={ref}
    className={cn(
      'c-classroom-module-header',
      'flex flex-wrap items-center justify-between gap-2',
      'p-3',
      'relative cursor-pointer',

      className
    )}
    {...props}
  />
));
ClassroomModuleHeader.displayName = 'ClassroomModuleHeader';

const ClassroomModuleNotificationNewMessage = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-classroom-module-notification-new-message', 'h-5 w-5 rounded-full bg-third-light text-xs text-white', 'flex flex-wrap items-center justify-center', 'absolute right-[-5px] top-[-5px]', className)}
    {...props}
  />
));
ClassroomModuleNotificationNewMessage.displayName = 'ClassroomModuleNotificationNewMessage';

const ClassroomModuleHeaderContent = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-classroom-module-header-content', 'flex flex-grow flex-wrap items-center justify-between gap-2', className)}
    {...props}
  />
));
ClassroomModuleHeaderContent.displayName = 'ClassroomModuleHeaderContent';

const ClassroomModuleIcon = React.forwardRef(({ className, isDisabled, isDone, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-classroom-module-icon',
      // Estilo base
      'h-6 w-6 flex-shrink-0 rounded-full border border-solid border-neutral-light',
      'flex flex-wrap items-center justify-center',
      // SVG
      '[&_svg]:size-[14px] [&_svg]:stroke-[2]',
      isDisabled && 'opacity-30',
      isDone && 'border-success bg-success [&_svg]:text-white',
      className
    )}
    {...props}
  />
));
ClassroomModuleIcon.displayName = 'ClassroomModuleIcon';

const ClassroomModuleTitle = React.forwardRef(({ className, isDisabled = false, ...props }, ref) => (
  <h4
    ref={ref}
    className={cn(
      'c-classroom-module-title',
      'text-sm font-bold text-primary-dark',
      'mb-0 w-0 flex-grow overflow-hidden text-ellipsis whitespace-nowrap',
      // Variação
      isDisabled && 'opacity-30',
      className
    )}
    {...props}
  />
));
ClassroomModuleTitle.displayName = 'ClassroomModuleTitle';

const ClassroomModuleActions = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-classroom-module-actions', 'flex flex-wrap items-center gap-2', className)}
    {...props}
  />
));
ClassroomModuleActions.displayName = 'ClassroomModuleActions';

const ClassroomModuleCount = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-classroom-module-count', 'text-sm font-light text-neutral', className)}
    {...props}
  />
));
ClassroomModuleCount.displayName = 'ClassroomModuleCount';

const ClassroomModuleBody = React.forwardRef(({ className, isActive, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-classroom-module-body', 'hidden animate-fade-in rounded-b-[12px] bg-bright', isActive && 'block', className)}
    {...props}
  />
));
ClassroomModuleBody.displayName = 'ClassroomModuleBody';

const ClassroomModuleItem = React.forwardRef(({ className, isActive = false, isDisabled = false, isRequirement = false, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'div';

  return (
    <Comp
      ref={ref}
      className={cn(
        'c-classroom-module-item',
        'flex w-full flex-wrap items-center gap-2 text-sm',
        'cursor-pointer px-3 py-[10px] transition-colors duration-150',
        'hover:bg-white',
        isActive && cn('bg-primary text-white hover:bg-primary', '[&_.c-classroom-module-item-title]:font-bold [&_.c-classroom-module-item-title]:text-white'),
        isDisabled && 'pointer-events-none cursor-default opacity-50',
        isRequirement && '[&_.c-classroom-module-item-title]:underline',
        'last:rounded-b-xl',
        className
      )}
      {...props}
    />
  );
});
ClassroomModuleItem.displayName = 'ClassroomModuleItem';

const ClassroomModuleItemTitle = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('c-classroom-module-item-title', 'text-neutral', 'mb-0 w-0 flex-grow overflow-hidden text-ellipsis whitespace-nowrap', className)}
    {...props}
  />
));
ClassroomModuleItemTitle.displayName = 'ClassroomModuleItemTitle';

const ClassroomModuleItemIcon = React.forwardRef(({ className, isDone = false, isRequirement = false, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(
      'c-classroom-module-item-icon',
      'ml-0 flex flex-initial list-none flex-wrap items-center justify-center',
      'h-[18px] w-[18px] rounded-full bg-white',
      'border border-solid border-primary-lighter',
      '[&_svg]:text-success',
      isDone && 'border-success',
      isRequirement && cn('c-classroom-module-item-icon-requirement', 'rounded-none border-0 bg-transparent', '[&_svg]:h-4 [&_svg]:w-4 [&_svg]:text-neutral'),
      className
    )}
    {...props}
  />
));
ClassroomModuleItemIcon.displayName = 'ClassroomModuleItemIcon';

const ClassroomModuleItemMessages = React.forwardRef(({ className, isNew = false, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-classroom-module-item-messages',
      'flex flex-shrink-0 items-center justify-center gap-0.5',
      'w-10 rounded px-0 py-0.5 text-[10px]',
      'border border-solid border-neutral-light',
      isNew && cn('bg-third text-white', 'border-solid border-third-light'),

      className
    )}
    {...props}
  />
));
ClassroomModuleItemMessages.displayName = 'ClassroomModuleItemMessages';

export {
  ClassroomModuleList,
  ClassroomModule,
  ClassroomModuleHeader,
  ClassroomModuleNotificationNewMessage,
  ClassroomModuleHeaderContent,
  ClassroomModuleIcon,
  ClassroomModuleTitle,
  ClassroomModuleActions,
  ClassroomModuleCount,
  ClassroomModuleBody,
  ClassroomModuleItem,
  ClassroomModuleItemTitle,
  ClassroomModuleItemIcon,
  ClassroomModuleItemMessages
};
